import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Button, Typography, Stack, Modal, Avatar, IconButton, Divider, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-hot-toast';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

const { PRIVATE_ROUTE } = API_ROUTES;

const TEAL = '#65C8D0';
const TEAL_DARK = '#3DADB5';
const TEAL_LIGHT = '#E0F6F8';
const TEAL_MID = '#4BBBC4';

const MAX_COMMENT_LENGTH = 500;
const MIN_COMMENT_LENGTH = 3;

const CommentsModal = ({ opentStatus, setCommentModal, selectedSchedule, user, onCommentAdded }) => {
  const [new_comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { authAxios } = useContext(FetchContext);
  const [comments, setComments] = useState([]);

  const validateComment = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return 'Comment cannot be empty.';
    if (trimmed.length < MIN_COMMENT_LENGTH) return `Comment must be at least ${MIN_COMMENT_LENGTH} characters.`;
    if (value.length > MAX_COMMENT_LENGTH) return `Comment cannot exceed ${MAX_COMMENT_LENGTH} characters.`;
    return '';
  };

  const handleCommentChange = ({ target: { value } }) => {
    if (value.length <= MAX_COMMENT_LENGTH) {
      setComment(value);
      if (commentError) setCommentError(validateComment(value));
    }
  };
  const handleSaveComment = async () => {
    const error = validateComment(new_comment);
    if (error) {
      setCommentError(error);
      return;
    }

    if (!selectedSchedule?._id || !user?._id) {
      toast.error('Missing schedule or user information.');
      return;
    }

    setIsSubmitting(true);
    try {
      const createdData = await authAxios.post(`${PRIVATE_ROUTE?.CREATE_COMMENT}`, {
        schedule_id: selectedSchedule._id,
        user_id: user._id,
        remark: new_comment.trim(),
        is_deleted: false,
      });

      if (createdData?.data?.statusCode === 400) {
        return toast.error('Could not save the comment.');
      }
      if (createdData?.data?.statusCode === 200) {
        setComments([
          {
            id: createdData.data.data_id,
            text: createdData.data.data.remark,
            name: `${user.first_name} ${user.last_name}`,
          },
          ...comments,
        ]);
        setComment('');
        setCommentError('');
        onCommentAdded?.();
      }
    } catch (err) {
      toast.error('Failed to post the comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFetchComments = async () => {
    if (!selectedSchedule?._id) return;
    try {
      const fetchData = await authAxios.get(
        `${PRIVATE_ROUTE?.FETCH_ALL_COMMENTS}?schedule_id=${selectedSchedule._id}`
      );

      if (fetchData?.data?.statusCode === 400) {
        return toast.error('Could not fetch comments.');
      }

      const fetched = (fetchData?.data?.data || []).map((item) => ({
        id: item._id,
        text: item.remark,
        name: `${item.user?.user_first_name || ''} ${item.user?.user_last_name || ''}`.trim(),
      }));

      setComments(fetched);
    } catch (err) {
      toast.error('Failed to load comments. Please try again.');
    }
  };

  useEffect(() => {
    handleFetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0][0].toUpperCase();
  };

  return (
    <Modal open={opentStatus}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: 560 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '85vh',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CommentIcon sx={{ color: '#fff', fontSize: 20 }} />
            <Box>
              <Typography
                sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', letterSpacing: 1.2, fontWeight: 600 }}
              >
                SCHEDULING
              </Typography>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                Comments
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setCommentModal(false)} sx={{ color: '#fff' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Comment Input */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, flexShrink: 0, borderBottom: `1px solid ${TEAL_LIGHT}` }}>
          <TextField
            placeholder="Write a comment..."
            multiline
            rows={3}
            fullWidth
            size="small"
            value={new_comment}
            onChange={handleCommentChange}
            error={!!commentError}
            helperText={
              commentError || (
                <Typography component="span" sx={{ fontSize: '0.72rem', color: new_comment.length >= MAX_COMMENT_LENGTH ? 'error.main' : '#aaa' }}>
                  {new_comment.length}/{MAX_COMMENT_LENGTH}
                </Typography>
              )
            }
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
              },
            }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCommentModal(false)}
              disabled={isSubmitting}
              sx={{
                borderColor: TEAL_MID,
                color: TEAL_DARK,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: TEAL_LIGHT, borderColor: TEAL_DARK },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              endIcon={isSubmitting ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: '14px !important' }} />}
              onClick={handleSaveComment}
              disabled={isSubmitting || !new_comment.trim()}
              sx={{
                backgroundColor: TEAL_DARK,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { backgroundColor: TEAL, boxShadow: 'none' },
                '&.Mui-disabled': { backgroundColor: 'rgba(101,200,208,0.3)', color: '#fff' },
              }}
            >
              Post
            </Button>
          </Stack>
        </Box>

        {/* Comments List */}
        <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 2 }}>
          {comments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CommentIcon sx={{ fontSize: 36, color: TEAL_LIGHT, mb: 1 }} />
              <Typography sx={{ color: '#aaa', fontSize: '0.85rem' }}>No comments yet.</Typography>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Divider sx={{ borderColor: TEAL_LIGHT }} />}>
              {comments.map((c, index) => (
                <Box key={c.id || index} sx={{ py: 1.75, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      backgroundColor: TEAL_MID,
                      color: '#fff',
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(c.name)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: TEAL_DARK, mb: 0.3 }}>
                      {c.name || ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#444', lineHeight: 1.5, wordBreak: 'break-word' }}>
                      {c.text || ''}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

CommentsModal.propTypes = {
  opentStatus: PropTypes.bool.isRequired,
  setCommentModal: PropTypes.func.isRequired,
  selectedSchedule: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  }).isRequired,
  onCommentAdded: PropTypes.func,
};

export default CommentsModal;
